import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { VendorDetail } from '@/components/analytics';
import { useVendorSummary, useCanvasserStats, useContactAnalytics, useVendorPerformance } from '@/hooks/queries';
import type { CanvasserAnalyticsFilters } from '@/lib/queryKeys';
import { ReadNotice } from '@/components/ui';
import { isLoading, readyData } from '@/lib/readParts';

export const Route = createFileRoute('/app/analytics/vendors/$vendorId')({
  component: VendorDetailRoute,
});

function VendorDetailRoute() {
  const { vendorId } = Route.useParams();
  const filters = Route.useSearch() as CanvasserAnalyticsFilters;
  const navigate = useNavigate();

  const summaryRead = useVendorSummary({ ...filters, vendorId });
  const statsRead = useCanvasserStats({ ...filters, vendorId });
  const vendorSummary = readyData(summaryRead);
  const canvasserStatsResponse = readyData(statsRead);
  const canvasserStats = canvasserStatsResponse?.items;
  const benchmarks = canvasserStatsResponse?.benchmarks;

  // VDA-002: Vendor performance trend
  const performanceRead = useVendorPerformance(vendorId, { dateFrom: filters.dateFrom, dateTo: filters.dateTo });

  // VDA-003: Vendor contact outcomes (vendor-scoped) and campaign-wide (for comparison)
  const vendorContactRead = useContactAnalytics(undefined, vendorId);
  const campaignContactRead = useContactAnalytics();
  const vendorContactData = readyData(vendorContactRead);
  const campaignContactData = readyData(campaignContactRead);
  const unready = [summaryRead, statsRead, performanceRead, vendorContactRead, campaignContactRead].find(
    (r) => r.status === 'failed' || r.status === 'offline',
  );

  const handleBack = () => {
    navigate({
      to: '/app/analytics',
      search: filters,
    });
  };

  const handleCanvasserClick = (userId: string) => {
    navigate({
      to: '/app/analytics/canvassers/$userId',
      params: { userId },
      search: filters,
    });
  };

  return (
    <>
    {unready && <ReadNotice read={unready} subject="some of this vendor's analytics" />}
    <VendorDetail
      vendorId={vendorId}
      vendorSummary={vendorSummary ?? []}
      canvasserStats={canvasserStats ?? []}
      isLoadingSummary={isLoading(summaryRead)}
      isLoadingStats={isLoading(statsRead)}
      onBack={handleBack}
      onCanvasserClick={handleCanvasserClick}
      benchmarks={benchmarks}
      vendorContactOutcomes={vendorContactData?.contactsByStatus}
      campaignContactOutcomes={campaignContactData?.contactsByStatus}
      isLoadingContactOutcomes={isLoading(vendorContactRead)}
      vendorPerformance={readyData(performanceRead)}
      isLoadingVendorPerformance={isLoading(performanceRead)}
    />
    </>
  );
}
